import React, { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { IssueType } from '../types'

interface IssueSelectorProps {
  selectedIssues: IssueType[]
  availableIssues: IssueType[]
  onChange: (issues: IssueType[]) => void
  placeholder?: string
  disabled?: boolean
}

export const IssueSelector: React.FC<IssueSelectorProps> = ({
  selectedIssues,
  availableIssues,
  onChange,
  placeholder = "Select issues...",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleToggleIssue = (issue: IssueType) => {
    if (selectedIssues.includes(issue)) {
      onChange(selectedIssues.filter(i => i !== issue))
    } else {
      onChange([...selectedIssues, issue])
    }
  }

  const handleRemoveIssue = (issue: IssueType, event: React.MouseEvent) => {
    event.stopPropagation()
    onChange(selectedIssues.filter(i => i !== issue))
  }

  

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main selector button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {selectedIssues.length === 0 ? (
              <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
            ) : selectedIssues.length === 1 ? (
              <span className="truncate">{selectedIssues[0]}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedIssues.slice(0, 2).map((issue) => (
                  <span
                    key={issue}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                  >
                    {issue}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveIssue(issue, e)}
                      className="ml-1 hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedIssues.length > 2 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    +{selectedIssues.length - 2} more
                  </span>
                )}
              </div>
            )}
          </div>
          <ChevronDownIcon 
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {availableIssues.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              No issues available
            </div>
          ) : (
            availableIssues.map((issue) => (
              <label
                key={issue}
                className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedIssues.includes(issue)}
                  onChange={() => handleToggleIssue(issue)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <span className="text-sm text-gray-900 dark:text-white flex-1">
                  {issue}
                </span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}
